import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import { selectActiveBuilding, selectActiveBuildingId } from '@/features/building/buildingSlice'
import { closeAiAssistant } from './aiAssistantSlice'
import { useAiChatStream } from './useAiChatStream'

const AiAssistantDrawer = ({ open }) => {
  const dispatch = useDispatch()
  const buildingId = useSelector(selectActiveBuildingId)
  const activeBuilding = useSelector(selectActiveBuilding)
  const buildingLabel = activeBuilding?.name || (buildingId ? `объект #${buildingId}` : 'объект')

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Задайте вопрос по сессиям, долгам или тарифам объекта «${buildingLabel}».`,
    },
  ])
  const [input, setInput] = useState('')
  const { sendMessage, isStreaming, abort } = useAiChatStream(buildingId)
  const listRef = useRef(null)

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, isStreaming])

  const handleSend = async () => {
    const text = input.trim()
    if (!text || isStreaming) return

    const nextMessages = [...messages, { role: 'user', content: text }]
    setMessages(nextMessages)
    setInput('')

    let assistantText = ''
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }])

    await sendMessage(
      nextMessages.filter((m) => m.role === 'user' || m.role === 'assistant'),
      (token) => {
        assistantText += token
        setMessages((prev) => {
          const copy = [...prev]
          copy[copy.length - 1] = { role: 'assistant', content: assistantText }
          return copy
        })
      },
      () => {},
      (err) => {
        setMessages((prev) => {
          const copy = [...prev]
          copy[copy.length - 1] = {
            role: 'assistant',
            content: err?.message || 'Ошибка чата',
          }
          return copy
        })
      }
    )
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        abort()
        dispatch(closeAiAssistant())
      }}
      slotProps={{
        paper: {
          sx: { width: { xs: '100%', sm: 400 }, display: 'flex', flexDirection: 'column' },
        }
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SmartToyOutlinedIcon color="primary" />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap>
            ИИ-ассистент
          </Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: "text.secondary",
              display: "block"
            }}>
            {buildingLabel}
          </Typography>
        </Box>
        <IconButton
          onClick={() => {
            abort()
            dispatch(closeAiAssistant())
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        ref={listRef}
        sx={{ flexGrow: 1, overflow: 'auto', px: 2, py: 1, display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        {messages.map((msg, idx) => (
          <Paper
            key={`${msg.role}-${idx}`}
            variant="outlined"
            sx={{
              p: 1.5,
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '90%',
              bgcolor: msg.role === 'user' ? 'primary.main' : 'background.paper',
              color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {msg.content}
              {isStreaming && idx === messages.length - 1 && msg.role === 'assistant' ? '▍' : ''}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Спросите про сессии, долги, тарифы..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSend()
            }
          }}
          disabled={isStreaming}
        />
        <IconButton color="primary" onClick={handleSend} disabled={isStreaming || !input.trim()}>
          <SendIcon />
        </IconButton>
      </Box>
    </Drawer>
  );
}

export default AiAssistantDrawer
